<script lang="ts">
	import { Container, Icon, Input, SelectV2, Text, ToggleV2 } from '@Shelly/shelly-design-system';
	import { devices_config } from '@devices/index';
	import { format } from 'svelte-i18n';
	import { ENERGY_MONITORING_PERIODS } from 'src/components/EnergyMonitoring/helpers/period_helpers';
	import { devices } from 'src/stores/lists/devices';
	import { getDefaultEnergyConfigForType, getRoomsWithPmDevices } from '../../dash-energy-helpers';
	import type { DashWidgetEditType } from '../../dash-types';
	import type { DashEnergyWidgetTypes } from '../../dash-energy-types';
	import { getEnergyWidgetDefinition } from '../../dash-energy-registry';
	import DashEnergyHeatmapScopeStep from './DashEnergyHeatmapScopeStep.svelte';

	export let editItem: DashWidgetEditType;

	$: widgetType = editItem.widgetType as DashEnergyWidgetTypes;
	$: widgetDefinition = getEnergyWidgetDefinition(widgetType);
	$: if (!editItem.energyConfig) {
		editItem.energyConfig = getDefaultEnergyConfigForType(widgetType);
	}

	$: isEnergyInfo = widgetType === 'dashEnergyInfo';
	$: includesCostMetric =
		isEnergyInfo &&
		(editItem.selectItems.some((item) => String(item.id) === 'tariff') ||
			editItem.energyConfig?.infoCategories?.includes('tariff'));
	$: includesPeriodMetric =
		isEnergyInfo &&
		['periodConsumption', 'periodGeneration', 'tariff', 'offPeak', 'peakDemand'].some(
			(id) =>
				editItem.selectItems.some((item) => String(item.id) === id) ||
				editItem.energyConfig?.infoCategories?.includes(
					id as NonNullable<typeof editItem.energyConfig>['infoCategories'][number],
				),
		);
	$: isRoomChart = widgetType === 'dashEnergyRoomChart';
	$: isTopConsumers = widgetType === 'dashEnergyTopConsumers';
	$: isConsumerMix = widgetType === 'dashEnergyConsumerMix';
	$: isFlow = widgetType === 'dashEnergyFlow';
	$: isHeatmap = widgetType === 'dashEnergyHeatmap';
	$: isPeak = widgetType === 'dashEnergyPeak';
	$: isDeviceTrend = widgetType === 'dashEnergyDeviceTrend';
	$: isRoomCharts = widgetType === 'dashEnergyRoomCharts';
	$: isRoomDistribution = widgetType === 'dashEnergyRoomDistribution';
	$: isMinimalHeatmap = isHeatmap && editItem.energyConfig?.heatmapVariant === 'minimal';
	$: isKpiPeriod = widgetType === 'dashEnergyKpiPeriod';
	$: supportsPeriod =
		widgetDefinition.supportsPeriod === true || (isEnergyInfo && includesPeriodMetric);

	$: settingsMeta = widgetDefinition.settings
		? {
				title: $format(widgetDefinition.settings.titleKey, {
					default: widgetDefinition.settings.titleDefault,
				}),
				description: $format(widgetDefinition.settings.descriptionKey, {
					default: widgetDefinition.settings.descriptionDefault,
				}),
				icon: widgetDefinition.settings.icon,
			}
		: {
				title: $format('label_dash_energy_widget_settings', {
					default: 'Energy widget settings',
				}),
				description: $format('dash_energy_settings_default_description', {
					default: 'Choose how this widget should appear on your dashboard.',
				}),
				icon: 'fal fa-bolt',
			};

	$: periodOptions = ENERGY_MONITORING_PERIODS.filter((option) => option.id !== 'live').map(
		(option) => ({
			value: option.id === 'live' ? 'live' : option.period,
			title: $format(option.labelKey, { default: option.labelDefault }),
		}),
	);

	$: selectedPeriodOption =
		periodOptions.find((option) => option.value === editItem.energyConfig?.period) ??
		periodOptions[0];

	$: heatmapMetricOptions = [
		{ value: 'consumption', title: $format('chart_legend_consumption', { default: 'Consumption' }) },
		{
			value: 'generation',
			title: $format('energy_monitoring_flow_generation', { default: 'Generation' }),
		},
	];
	$: selectedHeatmapMetricOption =
		heatmapMetricOptions.find((option) => option.value === editItem.energyConfig?.heatmapMetric) ??
		heatmapMetricOptions[0];

	$: peakVariantOptions = [
		{
			value: 'overview',
			title: $format('energy_monitoring_peak_overview_title', {
				default: 'Peak Power Overview',
			}),
		},
		{
			value: 'byMeter',
			title: $format('energy_monitoring_peak_by_meter_title', {
				default: 'Peak Power by Meter',
			}),
		},
		{
			value: 'phaseBalance',
			title: $format('energy_monitoring_phase_peak_balance_title', {
				default: 'Phase Peak Balance',
			}),
		},
	];
	$: selectedPeakVariantOption =
		peakVariantOptions.find((option) => option.value === editItem.energyConfig?.peakVariant) ??
		peakVariantOptions[0];

	$: chartTypeOptions = [
		{ value: 'bar', title: 'Bar' },
		{ value: 'areaspline', title: 'Area' },
	];
	$: selectedChartTypeOption =
		chartTypeOptions.find(
			(option) => option.value === (editItem.energyConfig?.chartType ?? 'bar'),
		) ?? chartTypeOptions[0];

	$: groupByOptions = [
		{ value: 'device', title: $format('devices', { default: 'Devices' }) },
		{ value: 'room', title: $format('tab_rooms', { default: 'Rooms' }) },
	];
	$: selectedGroupByOption =
		groupByOptions.find((option) => option.value === (editItem.energyConfig?.groupBy ?? 'device')) ??
		groupByOptions[0];

	$: supplyModeOptions = [
		{
			value: 'auto',
			title: $format('energy_monitoring_flow_supply_auto', {
				default: 'Automatic — measured when available',
			}),
		},
		{
			value: 'measured',
			title: $format('energy_monitoring_flow_supply_measured', {
				default: 'Measured only',
			}),
		},
		{
			value: 'calculated',
			title: $format('energy_monitoring_flow_supply_calculated', {
				default: 'Calculated from loads',
			}),
		},
	];
	$: selectedSupplyModeOption =
		supplyModeOptions.find(
			(option) => option.value === (editItem.energyConfig?.supplyMode ?? 'auto'),
		) ?? supplyModeOptions[0];

	$: powerMeterOptions = [
		{
			value: 'auto',
			title: $format('dash_energy_device_auto', { default: 'Automatic — top consumer' }),
		},
		...Object.values($devices)
			.filter((device) => device && devices_config[device.type]?.isPM)
			.sort((a, b) => a.name.localeCompare(b.name))
			.map((device) => ({ value: String(device.id), title: device.name })),
	];
	$: selectedPowerMeterOption =
		powerMeterOptions.find((option) => option.value === editItem.energyConfig?.deviceId) ??
		powerMeterOptions[0];

	$: monitoredRoomOptions = [
		{ value: 'all', title: $format('dash_energy_rooms_all', { default: 'All monitored rooms' }) },
		...getRoomsWithPmDevices().map((room) => ({ value: String(room.id), title: room.name })),
	];
	$: selectedRoomOption =
		monitoredRoomOptions.find(
			(option) => option.value === String(editItem.energyConfig?.roomIds?.[0] ?? 'all'),
		) ?? monitoredRoomOptions[0];

	const getOptionValue = (detail: unknown): string =>
		typeof detail === 'string'
			? detail
			: String((detail as { value?: string | number } | null)?.value ?? '');

	const getInputValue = (event: Event): string => (event.currentTarget as HTMLInputElement).value;
</script>

<div class="energy-settings-intro">
	<div class="energy-settings-icon" aria-hidden="true">
		<Icon icon={settingsMeta.icon} size="m" />
	</div>
	<div class="min-width-0">
		<Text styleType="controls-large" class="text-800 mb-xxs">{settingsMeta.title}</Text>
		<Text styleType="body-small" class="text-color-muted">{settingsMeta.description}</Text>
	</div>
</div>

<Container class="energy-settings-panel d-flex flex-direction-column gap-l">
	{#if isHeatmap}
		<section class="energy-settings-section">
			<div class="energy-settings-section-heading">
				<Text styleType="body" class="text-700">
					{$format('dash_energy_settings_scope_heading', { default: 'Data coverage' })}
				</Text>
				<Text styleType="body-small" class="text-color-muted">
					{$format('dash_energy_settings_scope_help', {
						default:
							'Choose whether the heatmap represents the whole home, one room, or one device.',
					})}
				</Text>
			</div>
			<DashEnergyHeatmapScopeStep {editItem} compact />
		</section>
	{/if}

	{#if isDeviceTrend}
		<section class="energy-settings-section">
			<div class="energy-settings-section-heading">
				<Text styleType="body" class="text-700">
					{$format('label_device', { default: 'Device' })}
				</Text>
				<Text styleType="body-small" class="text-color-muted">
					{$format('dash_energy_settings_device_help', {
						default:
							'Automatic follows the strongest current consumer; selecting a device keeps the widget fixed to it.',
					})}
				</Text>
			</div>
			<div class="energy-settings-field">
				<Text styleType="label-large">
					{$format('dash_energy_settings_device_label', { default: 'Power-meter device' })}
				</Text>
				<SelectV2
					id="dash-energy-device"
					icon={null}
					aria-label={$format('label_device', { default: 'Device' })}
					size="m"
					width="full"
					label={$format('dash_energy_settings_device_label', {
						default: 'Power-meter device',
					})}
					options={powerMeterOptions}
					value={selectedPowerMeterOption}
					on:change={(e) => {
						const value = getOptionValue(e.detail);
						editItem.energyConfig = {
							...editItem.energyConfig,
							deviceId: value === 'auto' ? undefined : value,
						};
					}}
				/>
			</div>
		</section>
	{/if}

	{#if isRoomCharts}
		<section class="energy-settings-section">
			<div class="energy-settings-section-heading">
				<Text styleType="body" class="text-700">
					{$format('label_widget_rooms', { default: 'Rooms' })}
				</Text>
				<Text styleType="body-small" class="text-color-muted">
					{$format('dash_energy_settings_rooms_help', {
						default: 'Show every monitored room or keep the overview focused on one room.',
					})}
				</Text>
			</div>
			<div class="energy-settings-field">
				<Text styleType="label-large">
					{$format('dash_energy_settings_rooms_label', { default: 'Rooms to show' })}
				</Text>
				<SelectV2
					id="dash-energy-room-overview"
					icon={null}
					aria-label={$format('label_widget_rooms', { default: 'Rooms' })}
					size="m"
					width="full"
					label={$format('dash_energy_settings_rooms_label', { default: 'Rooms to show' })}
					options={monitoredRoomOptions}
					value={selectedRoomOption}
					on:change={(e) => {
						const value = getOptionValue(e.detail);
						editItem.energyConfig = {
							...editItem.energyConfig,
							roomIds: value === 'all' ? [] : [Number(value)],
						};
					}}
				/>
			</div>
		</section>
	{/if}

	{#if supportsPeriod}
		<section class="energy-settings-section">
			<div class="energy-settings-section-heading">
				<Text styleType="body" class="text-700">
					{$format('dash_energy_settings_time_heading', { default: 'Time range' })}
				</Text>
				<Text styleType="body-small" class="text-color-muted">
					{$format('dash_energy_settings_time_help', {
						default:
							'The widget refreshes automatically and uses this range for its comparison and totals.',
					})}
				</Text>
			</div>
			<div class="energy-settings-field">
				<Text styleType="label-large">{$format('label_period', { default: 'Period' })}</Text>
				<SelectV2
					id="dash-energy-period"
					icon={null}
					aria-label={$format('label_period', { default: 'Period' })}
					size="m"
					width="full"
					label={$format('label_period', { default: 'Period' })}
					options={periodOptions}
					value={selectedPeriodOption}
					on:change={(e) => {
						editItem.energyConfig = {
							...editItem.energyConfig,
							period: getOptionValue(e.detail) as NonNullable<
								typeof editItem.energyConfig
							>['period'],
						};
					}}
				/>
			</div>
		</section>
	{:else}
		<div class="energy-settings-note">
			<Icon icon={isHeatmap ? 'fal fa-calendar-days' : 'fal fa-wave-pulse'} size="s" />
			<Text styleType="body-small">
				{isHeatmap
					? $format('dash_energy_heatmap_calendar_hint', {
							default:
								'The full heatmap follows the current year; the compact heatmap follows the current month.',
						})
					: $format('dash_energy_kpi_live_period_hint', {
							default: 'This widget uses live readings and updates automatically.',
						})}
			</Text>
		</div>
	{/if}

	<section class="energy-settings-section">
		<div class="energy-settings-section-heading">
			<Text styleType="body" class="text-700">
				{$format('dash_energy_settings_header_heading', { default: 'Widget header' })}
			</Text>
			<Text styleType="body-small" class="text-color-muted">
				{$format('dash_energy_settings_header_help', {
					default: 'Choose whether the selected time period appears beside the widget name.',
				})}
			</Text>
		</div>
		<div class="energy-settings-toggle">
			<div class="energy-settings-toggle-control">
				<Text styleType="body" class="text-700">
					{$format('dash_energy_show_period_label', { default: 'Show selected period' })}
				</Text>
				<ToggleV2
					id="dash-energy-show-period-label"
					label={$format('dash_energy_show_period_label', {
						default: 'Show selected period',
					})}
					checked={editItem.energyConfig.showPeriodLabel !== false}
					on:change={(e) =>
						(editItem.energyConfig = {
							...editItem.energyConfig,
							showPeriodLabel: e.detail.checked,
						})}
				/>
			</div>
			<Text styleType="body-small" class="text-color-muted">
				{$format('dash_energy_show_period_label_help', {
					default: 'Keeps the active range visible in the widget section header.',
				})}
			</Text>
		</div>
	</section>

	{#if isTopConsumers}
		<section class="energy-settings-section">
			<div class="energy-settings-section-heading">
				<Text styleType="body" class="text-700">
					{$format('dash_energy_settings_ranking_heading', { default: 'Ranking' })}
				</Text>
				<Text styleType="body-small" class="text-color-muted">
					{$format('dash_energy_settings_top_description', {
						default:
							'Choose whether the five largest live consumers are grouped by device or room.',
					})}
				</Text>
			</div>
			<div class="energy-settings-grid">
				<div class="energy-settings-field">
					<Text styleType="label-large"
						>{$format('label_group_by', { default: 'Group by' })}</Text
					>
					<SelectV2
						id="dash-energy-group-by"
						icon={null}
						aria-label={$format('label_group_by', { default: 'Group by' })}
						size="m"
						width="full"
						label={$format('label_group_by', { default: 'Group by' })}
						options={groupByOptions}
						value={selectedGroupByOption}
						on:change={(e) =>
							(editItem.energyConfig = {
								...editItem.energyConfig,
								groupBy: getOptionValue(e.detail) as 'device' | 'room',
							})}
					/>
				</div>
			</div>
		</section>
	{/if}

	{#if isRoomDistribution}
		<section class="energy-settings-section">
			<div class="energy-settings-section-heading">
				<Text styleType="body" class="text-700">
					{$format('dash_energy_settings_room_distribution_display_heading', {
						default: 'Room visibility',
					})}
				</Text>
				<Text styleType="body-small" class="text-color-muted">
					{$format('dash_energy_settings_room_distribution_display_help', {
						default: 'Choose whether rooms without consumption appear in the comparison.',
					})}
				</Text>
			</div>
			<div class="energy-settings-toggle">
				<div class="energy-settings-toggle-control">
					<Text styleType="body" class="text-700">
						{$format('dash_energy_show_zero_consumption_rooms', {
							default: 'Show rooms with zero consumption',
						})}
					</Text>
					<ToggleV2
						id="dash-energy-show-zero-consumption-rooms"
						label={$format('dash_energy_show_zero_consumption_rooms', {
							default: 'Show rooms with zero consumption',
						})}
						checked={editItem.energyConfig.showZeroConsumptionRooms === true}
						on:change={(e) =>
							(editItem.energyConfig = {
								...editItem.energyConfig,
								showZeroConsumptionRooms: e.detail.checked,
							})}
					/>
				</div>
				<Text styleType="body-small" class="text-color-muted">
					{$format('dash_energy_show_zero_consumption_rooms_help', {
						default: 'Off by default so the chart stays focused on rooms that used energy.',
					})}
				</Text>
			</div>
		</section>
	{/if}

	{#if isConsumerMix}
		<section class="energy-settings-section">
			<div class="energy-settings-section-heading">
				<Text styleType="body" class="text-700"
					>{$format('sub_settings_title_display', { default: 'Display' })}</Text
				>
				<Text styleType="body-small" class="text-color-muted"
					>{$format('dash_energy_settings_mix_help', {
						default: 'Consumers below the minimum share are combined into Other.',
					})}</Text
				>
			</div>
			<div class="energy-settings-field">
				<Text styleType="label-large">
					{$format('label_min_share_percent', { default: 'Minimum share %' })}
				</Text>
				<Input
					id="dash-energy-min-share"
					aria-label={$format('label_min_share_percent', { default: 'Minimum share %' })}
					size="m"
					type="number"
					min="0"
					max="100"
					label={$format('label_min_share_percent', { default: 'Minimum share %' })}
					value={String(editItem.energyConfig.minSharePercent ?? 3)}
					on:input={(e) =>
						(editItem.energyConfig = {
							...editItem.energyConfig,
							minSharePercent: Math.min(100, Math.max(0, Number(getInputValue(e)) || 0)),
						})}
				/>
			</div>
			<div class="energy-settings-toggle">
				<div class="energy-settings-toggle-control">
					<Text styleType="body" class="text-700"
						>{$format('label_show_legend', { default: 'Show legend' })}</Text
					>
					<ToggleV2
						id="dash-energy-show-legend"
						label={$format('label_show_legend', { default: 'Show legend' })}
						checked={editItem.energyConfig.showLegend !== false}
						on:change={(e) =>
							(editItem.energyConfig = {
								...editItem.energyConfig,
								showLegend: e.detail.checked,
							})}
					/>
				</div>
				<Text styleType="body-small" class="text-color-muted"
					>{$format('dash_energy_settings_legend_help', {
						default: 'Keep color labels visible below the chart.',
					})}</Text
				>
			</div>
		</section>
	{/if}

	{#if isRoomChart}
		<section class="energy-settings-section">
			<div class="energy-settings-section-heading">
				<Text styleType="body" class="text-700"
					>{$format('dash_energy_settings_chart_heading', {
						default: 'Chart appearance',
					})}</Text
				>
				<Text styleType="body-small" class="text-color-muted"
					>{$format('dash_energy_settings_chart_help', {
						default:
							'Bars make intervals easy to compare; area emphasizes the overall trend.',
					})}</Text
				>
			</div>
			<div class="energy-settings-field">
				<Text styleType="label-large"
					>{$format('chart_more_options_chart_type_label', { default: 'Chart type' })}</Text
				>
				<SelectV2
					id="dash-energy-chart-type"
					icon={null}
					aria-label={$format('chart_more_options_chart_type_label', {
						default: 'Chart type',
					})}
					size="m"
					width="full"
					label={$format('chart_more_options_chart_type_label', { default: 'Chart type' })}
					options={chartTypeOptions}
					value={selectedChartTypeOption}
					on:change={(e) =>
						(editItem.energyConfig = {
							...editItem.energyConfig,
							chartType: getOptionValue(e.detail) as 'bar' | 'areaspline',
						})}
				/>
			</div>
		</section>
	{/if}

	{#if isFlow}
		<section class="energy-settings-section">
			<div class="energy-settings-section-heading">
				<Text styleType="body" class="text-700"
					>{$format('dash_energy_settings_supply_heading', { default: 'Home supply' })}</Text
				>
				<Text styleType="body-small" class="text-color-muted"
					>{$format('dash_energy_settings_supply_help', {
						default:
							'Use an entry meter when available, or calculate supply from the visible loads.',
					})}</Text
				>
			</div>
			<div class="energy-settings-toggle">
				<div class="energy-settings-toggle-control">
					<Text styleType="body" class="text-700"
						>{$format('energy_monitoring_flow_supply', {
							default: 'Show home supply',
						})}</Text
					>
					<ToggleV2
						id="dash-energy-show-supply"
						label={$format('energy_monitoring_flow_supply', { default: 'Show home supply' })}
						checked={editItem.energyConfig.showSupply !== false}
						on:change={(e) =>
							(editItem.energyConfig = {
								...editItem.energyConfig,
								showSupply: e.detail.checked,
							})}
					/>
				</div>
				<Text styleType="body-small" class="text-color-muted"
					>{$format('dash_energy_settings_supply_toggle_help', {
						default: 'Adds the electricity entry point as the first node in the flow.',
					})}</Text
				>
			</div>
			{#if editItem.energyConfig.showSupply !== false}
				<div class="energy-settings-field">
					<Text styleType="label-large"
						>{$format('energy_monitoring_flow_supply_mode', {
							default: 'Supply data source',
						})}</Text
					>
					<SelectV2
						id="dash-energy-supply-mode"
						icon={null}
						aria-label={$format('energy_monitoring_flow_supply_mode', {
							default: 'Supply data source',
						})}
						size="m"
						width="full"
						label={$format('energy_monitoring_flow_supply_mode', {
							default: 'Supply data source',
						})}
						options={supplyModeOptions}
						value={selectedSupplyModeOption}
						on:change={(e) =>
							(editItem.energyConfig = {
								...editItem.energyConfig,
								supplyMode: getOptionValue(e.detail) as
									| 'auto'
									| 'measured'
									| 'calculated',
							})}
					/>
				</div>
			{/if}
		</section>

		<section class="energy-settings-section">
			<div class="energy-settings-section-heading">
				<Text styleType="body" class="text-700"
					>{$format('dash_energy_settings_flow_nodes_heading', {
						default: 'Flow detail',
					})}</Text
				>
				<Text styleType="body-small" class="text-color-muted"
					>{$format('dash_energy_settings_flow_nodes_help', {
						default:
							'Rooms summarize their devices. Device nodes provide more detail but need more space.',
					})}</Text
				>
			</div>
			<div class="energy-settings-toggle">
				<div class="energy-settings-toggle-control">
					<Text styleType="body" class="text-700"
						>{$format('energy_monitoring_flow_rooms', { default: 'Show rooms' })}</Text
					>
					<ToggleV2
						id="dash-energy-show-rooms"
						label={$format('energy_monitoring_flow_rooms', { default: 'Show rooms' })}
						checked={editItem.energyConfig.showRooms !== false}
						on:change={(e) =>
							(editItem.energyConfig = {
								...editItem.energyConfig,
								showRooms: e.detail.checked,
							})}
					/>
				</div>
				<Text styleType="body-small" class="text-color-muted"
					>{$format('dash_energy_settings_flow_rooms_help', {
						default: 'Group monitored consumption by room.',
					})}</Text
				>
			</div>
			<div class="energy-settings-toggle">
				<div class="energy-settings-toggle-control">
					<Text styleType="body" class="text-700"
						>{$format('energy_monitoring_flow_devices', { default: 'Show devices' })}</Text
					>
					<ToggleV2
						id="dash-energy-show-devices"
						label={$format('energy_monitoring_flow_devices', { default: 'Show devices' })}
						checked={editItem.energyConfig.showDevices !== false}
						on:change={(e) =>
							(editItem.energyConfig = {
								...editItem.energyConfig,
								showDevices: e.detail.checked,
							})}
					/>
				</div>
				<Text styleType="body-small" class="text-color-muted"
					>{$format('dash_energy_settings_flow_devices_help', {
						default: 'Show individual measured loads after their room.',
					})}</Text
				>
			</div>
			<div class="energy-settings-grid">
				<div class="energy-settings-field">
					<Text styleType="label-large"
						>{$format('label_max_room_nodes', { default: 'Maximum rooms' })}</Text
					>
					<Input
						id="dash-energy-max-room-nodes"
						aria-label={$format('label_max_room_nodes', { default: 'Maximum rooms' })}
						size="m"
						type="number"
						min="1"
						max="12"
						label={$format('label_max_room_nodes', { default: 'Maximum rooms' })}
						value={String(editItem.energyConfig.maxRoomNodes ?? 6)}
						on:input={(e) =>
							(editItem.energyConfig = {
								...editItem.energyConfig,
								maxRoomNodes: Math.min(12, Math.max(1, Number(getInputValue(e)) || 6)),
							})}
					/>
				</div>
				<div class="energy-settings-field">
					<Text styleType="label-large"
						>{$format('label_max_device_nodes', { default: 'Maximum devices' })}</Text
					>
					<Input
						id="dash-energy-max-device-nodes"
						aria-label={$format('label_max_device_nodes', { default: 'Maximum devices' })}
						size="m"
						type="number"
						min="1"
						max="20"
						label={$format('label_max_device_nodes', { default: 'Maximum devices' })}
						value={String(editItem.energyConfig.maxDeviceNodes ?? 8)}
						on:input={(e) =>
							(editItem.energyConfig = {
								...editItem.energyConfig,
								maxDeviceNodes: Math.min(20, Math.max(1, Number(getInputValue(e)) || 8)),
							})}
					/>
				</div>
			</div>
		</section>
	{/if}

	{#if isHeatmap}
		<section class="energy-settings-section">
			<div class="energy-settings-section-heading">
				<Text styleType="body" class="text-700"
					>{$format('dash_energy_settings_heatmap_display_heading', {
						default: 'Heatmap display',
					})}</Text
				>
				<Text styleType="body-small" class="text-color-muted"
					>{$format('dash_energy_settings_heatmap_display_help', {
						default:
							'Consumption uses warm intensity colors; generation uses green intensity colors.',
					})}</Text
				>
			</div>
			<div class="energy-settings-field">
				<Text styleType="label-large"
					>{$format('dash_energy_heatmap_default_metric', { default: 'Default metric' })}</Text
				>
				<SelectV2
					id="dash-energy-heatmap-default-metric"
					icon={null}
					aria-label={$format('dash_energy_heatmap_default_metric', {
						default: 'Default metric',
					})}
					size="m"
					width="full"
					label={$format('dash_energy_heatmap_default_metric', { default: 'Default metric' })}
					options={heatmapMetricOptions}
					value={selectedHeatmapMetricOption}
					on:change={(e) =>
						(editItem.energyConfig = {
							...editItem.energyConfig,
							heatmapMetric: getOptionValue(e.detail) as 'consumption' | 'generation',
						})}
				/>
			</div>
			{#if !isMinimalHeatmap && editItem.energyConfig.heatmapScope === 'all'}
				<div class="energy-settings-toggle">
					<div class="energy-settings-toggle-control">
						<Text styleType="body" class="text-700"
							>{$format('dash_energy_heatmap_show_period_total', {
								default: 'Show period totals',
							})}</Text
						>
						<ToggleV2
							id="dash-energy-show-period-total"
							label={$format('dash_energy_heatmap_show_period_total', {
								default: 'Show period totals',
							})}
							checked={editItem.energyConfig.showPeriodTotal !== false}
							on:change={(e) =>
								(editItem.energyConfig = {
									...editItem.energyConfig,
									showPeriodTotal: e.detail.checked,
								})}
						/>
					</div>
					<Text styleType="body-small" class="text-color-muted"
						>{$format('dash_energy_settings_heatmap_totals_help', {
							default: 'Adds the tracked energy total below the calendar.',
						})}</Text
					>
				</div>
			{/if}
		</section>
	{/if}

	{#if isPeak}
		<section class="energy-settings-section">
			<div class="energy-settings-section-heading">
				<Text styleType="body" class="text-700"
					>{$format('dash_energy_settings_layout_heading', { default: 'Layout' })}</Text
				>
				<Text styleType="body-small" class="text-color-muted"
					>{$format('dash_energy_settings_peak_layout_help', {
						default:
							'Overview ranks channels, By meter compares meters, and Phase balance focuses on A, B, and C.',
					})}</Text
				>
			</div>
			<div class="energy-settings-field">
				<Text styleType="label-large"
					>{$format('energy_monitoring_peak_variant', { default: 'Peak power layout' })}</Text
				>
				<SelectV2
					id="dash-energy-peak-variant"
					icon={null}
					aria-label={$format('energy_monitoring_peak_variant', {
						default: 'Peak power layout',
					})}
					size="m"
					width="full"
					label={$format('energy_monitoring_peak_variant', { default: 'Peak power layout' })}
					options={peakVariantOptions}
					value={selectedPeakVariantOption}
					on:change={(e) =>
						(editItem.energyConfig = {
							...editItem.energyConfig,
							peakVariant: getOptionValue(e.detail) as NonNullable<
								typeof editItem.energyConfig
							>['peakVariant'],
						})}
				/>
			</div>
		</section>
	{/if}

	{#if isKpiPeriod || includesCostMetric}
		<section class="energy-settings-section">
			<div class="energy-settings-section-heading">
				<Text styleType="body" class="text-700"
					>{$format('dash_energy_settings_cost_heading', { default: 'Cost detail' })}</Text
				>
				<Text styleType="body-small" class="text-color-muted"
					>{$format('dash_energy_settings_cost_help', {
						default:
							'Tariff calculations use the electricity prices configured for your home.',
					})}</Text
				>
			</div>
			<div class="energy-settings-toggle">
				<div class="energy-settings-toggle-control">
					<Text styleType="body" class="text-700"
						>{$format('label_show_tariff_bar', { default: 'Show tariff mix bar' })}</Text
					>
					<ToggleV2
						id="dash-energy-show-tariff-bar"
						label={$format('label_show_tariff_bar', { default: 'Show tariff mix bar' })}
						checked={editItem.energyConfig.showTariffBar !== false}
						on:change={(e) =>
							(editItem.energyConfig = {
								...editItem.energyConfig,
								showTariffBar: e.detail.checked,
							})}
					/>
				</div>
				<Text styleType="body-small" class="text-color-muted"
					>{$format('dash_energy_settings_tariff_bar_help', {
						default: 'Shows how consumption is split across configured tariff bands.',
					})}</Text
				>
			</div>
		</section>
	{/if}
</Container>

<style>
	.energy-settings-intro {
		display: flex;
		align-items: flex-start;
		gap: var(--spacing-m);
		padding: var(--spacing-m);
		margin-bottom: var(--spacing-m);
		border: 1px solid color-mix(in srgb, var(--color-primary) 24%, transparent);
		border-radius: var(--border-radius-m);
		background: linear-gradient(
			135deg,
			color-mix(in srgb, var(--color-primary) 12%, transparent),
			transparent 72%
		);
	}

	.energy-settings-icon {
		display: grid;
		place-items: center;
		width: 40px;
		height: 40px;
		flex: 0 0 40px;
		border-radius: 12px;
		color: var(--color-primary);
		background: color-mix(in srgb, var(--color-primary) 18%, transparent);
	}

	.energy-settings-panel {
		padding: 0;
	}

	.energy-settings-section {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-m);
		padding: var(--spacing-m);
		border: 1px solid var(--color-2);
		border-radius: var(--border-radius-m);
		background: color-mix(in srgb, var(--color-1) 72%, transparent);
	}

	.energy-settings-section-heading,
	.energy-settings-toggle,
	.energy-settings-field {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xxs);
	}

	.energy-settings-toggle-control {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--spacing-m);
	}

	.energy-settings-toggle {
		padding-top: var(--spacing-xs);
	}

	.energy-settings-grid {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: var(--spacing-m);
	}

	.energy-settings-note {
		display: flex;
		align-items: flex-start;
		gap: var(--spacing-s);
		padding: var(--spacing-s) var(--spacing-m);
		border-radius: var(--border-radius-s);
		color: var(--color-primary);
		background: color-mix(in srgb, var(--color-primary) 10%, transparent);
	}

	@media (max-width: 560px) {
		.energy-settings-intro,
		.energy-settings-section {
			padding: var(--spacing-s-plus);
		}

		.energy-settings-grid {
			grid-template-columns: minmax(0, 1fr);
		}
	}
</style>
